/**
 * home-data.ts - Homepage redesign static content (Wave 0 foundation).
 *
 * Plain, serializable data only. NO JSX, NO component references. Every
 * value here is transcribed from the RENDERED design markup (the source of
 * truth), not from the dead `_loop*` prototype methods in the design file
 * (those carry stale copy and are ignored).
 *
 * No em dashes anywhere (team style rule); the founder line drops the
 * design's leading dash prefix so the section renders it dash-free.
 */

import type { HomeAccent } from "./home-accent";

export interface LoopStep {
  num: string;
  accent: HomeAccent;
  eyebrow: string;
  title: string;
  desc: string;
  windowLabel: string;
}

/**
 * The 6 pinned-loop steps. Copy taken from the rendered loop markup
 * (design L211-264 for eyebrow/title/desc, L275/289/303/317/331 for the
 * product-window chrome label).
 *
 * Steps 03, 05 and 06 carry the named capabilities (Altair Clear, Altair Live,
 * Altair Memory) so the loop answers the three problems raised by
 * WhereItBreaksSection immediately above it. Naming lives in the eyebrow only;
 * titles and descriptions are unchanged.
 *
 * CRITICAL: `VIZZES` in LoopProductWindow.tsx is a fixed six-element tuple
 * matched to this array BY POSITION with no length guard, and four consumers
 * are index-coupled. Renaming is safe. Adding, removing or reordering a step
 * is not. Descriptions clip silently past roughly 150 characters on desktop.
 */
export const LOOP_STEPS: LoopStep[] = [
  {
    num: "01",
    accent: "sky",
    eyebrow: "Connect",
    title: "Plugs into your systems",
    desc: "Connects to your EHR and clearinghouse. No rip and replace, and nothing new for your front desk to run.",
    windowLabel: "altair · integrations",
  },
  {
    num: "02",
    accent: "violet",
    eyebrow: "Verify",
    title: "Verified before the visit",
    desc: "Eligibility, prior auth, and pre-determination, all confirmed before the visit, the way each payer requires.",
    windowLabel: "eligibility · verifying",
  },
  {
    num: "03",
    accent: "amber",
    eyebrow: "Altair Clear",
    title: "Cleared before it ships",
    desc: "Every claim is checked against that payer's exact policies before it's sent, so it goes out clean and compliant the first time, not bounced back.",
    windowLabel: "claim · checking",
  },
  {
    num: "04",
    accent: "emerald",
    eyebrow: "Fight",
    title: "Every denial worked the moment it lands",
    desc: "When a denial lands, Altair works it and files the appeal right away, using how that payer behaves. Nothing waits in a queue, and you get paid.",
    windowLabel: "denials · auto-worked",
  },
  {
    num: "05",
    accent: "sky",
    eyebrow: "Altair Live",
    title: "Every dollar, in real time",
    desc: "Every claim, dollar, denial, and appeal in one live view. Where your money stands today, across patients, providers, and payers, not at month-end.",
    windowLabel: "altair · live financials",
  },
  {
    num: "06",
    accent: "violet",
    eyebrow: "Altair Memory",
    title: "It learns, and it remembers",
    desc: "Learns how each payer behaves and how your practice works, so the fix that got a claim paid gets reused the next time that payer does the same thing.",
    windowLabel: "altair · memory",
  },
];

/** EMR / clearinghouse marquee names (design L1043). */
export const EMR_NAMES: string[] = [
  "Epic",
  "athenahealth",
  "eClinicalWorks",
  "NextGen",
  "Tebra",
  "DrChrono",
  "AdvancedMD",
  "Availity",
  "Change Healthcare",
  "Office Ally",
  "Waystar",
  "Kareo",
];

/** Payer wall marquee - top row, scrolls left (design L1022). */
export const PAYERS_ROW_A: string[] = [
  "Aetna",
  "UnitedHealthcare",
  "Cigna",
  "Anthem",
  "Blue Cross Blue Shield",
  "Humana",
  "Medicare",
  "Kaiser Permanente",
];

/** Payer wall marquee - bottom row, scrolls right (design L1023). */
export const PAYERS_ROW_B: string[] = [
  "Medicaid",
  "Centene",
  "Molina",
  "Tricare",
  "WellCare",
  "Oscar",
  "Elevance",
  "Highmark",
];

/** Closing-CTA count-up figure: average dollars lost per practice per 90 days. */
export const LEAK_FIGURE = 54000;

/* ---------------------------------------------------------------------------
 * Investor strip (added 2026-09, after Wave 0).
 * ------------------------------------------------------------------------- */

export interface InvestorBrand {
  /** Brand name, used verbatim as the image alt text. */
  name: string;
  /** Same-origin path under apps/web/public/investors/. Production CSP is
      img-src 'self', so nothing here may point at another host. */
  src: string;
  /** Rendered size in px at full scale, tuned so the seven marks sit at equal
      optical weight (wordmark x-height, not bounding box). `width` is the
      intrinsic width at that height; both go on the <img> as attributes so
      the browser knows the aspect ratio before the SVG decodes and the row
      does not reflow on first paint. InvestorStrip scales both with a CSS
      variable at tablet and phone widths. */
  height: number;
  width: number;
}

/**
 * Household-name Y Combinator portfolio companies shown under "Backed by
 * Y Combinator, the investors behind". Selection rule (founder, 2026-09-19):
 * brands a US doctor or office manager recognises on sight, not brands tech
 * people know. Order is the locked display order, by public recognition
 * (YouGov Ratings, US adults who have heard of the brand: DoorDash 97%,
 * Airbnb 97%, Reddit 95%, Dropbox 74%; Statista 2024 aided awareness for
 * Instacart 83%), then Stripe and OpenAI by founder decision.
 *
 * Artwork is each brand's official vector logo, bytes unaltered, self-hosted
 * because of the CSP. Never recolour, restyle or link these marks. yc.svg is
 * the YC mark that leads the sentence.
 */
export const INVESTOR_BRANDS: InvestorBrand[] = [
  { name: "DoorDash", src: "/investors/doordash.svg", height: 20, width: 171 },
  { name: "Airbnb", src: "/investors/airbnb.svg", height: 31, width: 99 },
  { name: "Reddit", src: "/investors/reddit.svg", height: 28, width: 88 },
  {
    name: "Instacart",
    src: "/investors/instacart.svg",
    height: 24,
    width: 150,
  },
  { name: "Dropbox", src: "/investors/dropbox.svg", height: 26, width: 132 },
  { name: "Stripe", src: "/investors/stripe.svg", height: 34, width: 82 },
  { name: "OpenAI", src: "/investors/openai.svg", height: 26, width: 96 },
];

/** The sentence over the logos. Y Combinator is the grammatical subject on
    purpose: the logos are YC's portfolio, so the line stays literally true
    however a reader parses the row. The YC mark stands in for the "Y", so
    the first half is split around it; `second` starts the phone line break. */
export const INVESTOR_STRIP_LINE = {
  before: "Backed by",
  after: "Combinator,",
  second: "the investors behind",
} as const;
